import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { canAccess } from '../utils/canAccess';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import Select from 'react-select';
import {
  FaBroom,
  FaBuilding,
  FaCheckCircle,
  FaClipboardCheck,
  FaClock,
  FaEdit,
  FaExclamationTriangle,
  FaFileAlt,
  FaFileExcel,
  FaFilePdf,
  FaFilter,
  FaLock,
  FaMapMarkerAlt,
  FaOilCan,
  FaPaperclip,
  FaPhoneAlt,
  FaPlus,
  FaRoad,
  FaSearch,
  FaStickyNote,
  FaSyncAlt,
  FaTimes,
  FaTrash,
  FaTruck,
  FaUser,
  FaWrench
} from 'react-icons/fa';
import KpiCard from '../components/KpiCard';
import InfoTip from '../components/InfoTip';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { generateMaterielReparationsPDF } from '../utils/generateMaterielReparationsPDF';
import DocumentsModal from '../components/DocumentsModal';
import documentService from '../services/documentService';
import { normalizeModeCompteur } from '../utils/normalize';
import '../styles/MaterielPage.css';

const SimpleModal = ({ show, title, onClose, children, footer }) => {
  if (!show) return null;

  return (
    <div
      className="mp-modal-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 2000,
        padding: 16
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="mp-modal"
        style={{
          width: 'min(1200px, 100%)',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          overflow: 'hidden'
        }}
      >
        <div
          className="mp-modal-header"
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12
          }}
        >
          <div style={{ fontWeight: 800 }}>{title}</div>
          <Button variant="outline-secondary" size="sm" onClick={onClose} title="Fermer">
            <FaTimes />
          </Button>
        </div>

        <div className="mp-modal-body" style={{ padding: 16, maxHeight: '78vh', overflow: 'auto' }}>
          {children}
        </div>

        {footer && (
          <div
            className="mp-modal-footer"
            style={{
              padding: '12px 16px',
              borderTop: '1px solid #eee',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              flexWrap: 'wrap'
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};


const FormSection = ({ icon: Icon, title, children }) => (
  <div className="mp-form-section">
    <div className="mp-form-section-title">
      <Icon /> {title}
    </div>
    {children}
  </div>
);

const DEFAULTS_H = {
  interval: 250,
  warn: 190,
  crit: 230,
  tol: 20
};

const DEFAULTS_KM = {
  interval: 10000,
  warn: 8000,
  crit: 9500,
  tol: 500
};

const DEFAULTS_VISITE_TECHNIQUE = {
  cadence: 12,
  seuilAlerte: 30
};

const PAPIERS = [
  { key: 'visite_technique', label: 'Visite technique' },
  { key: 'vignette', label: 'Vignette' },
  { key: 'feuille_circulation', label: 'Feuille de circulation' },
  { key: 'assurance', label: 'Assurance' },
  { key: 'mouchard', label: 'Mouchard' }
];

const toDateInput = (d) => (d ? String(d).split('T')[0] : '');

const MaterielPage = ({ user }) => {
  const [materiels, setMateriels] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formMode, setFormMode] = useState('add');

  const [showDocsModal, setShowDocsModal] = useState(false);
  const [selectedDocItem, setSelectedDocItem] = useState(null);
  const [docCounts, setDocCounts] = useState({});

  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [pdfError, setPdfError] = useState('');

  const [toastError, setToastError] = useState('');
  const [toastSuccess, setToastSuccess] = useState('');

  const [globalSearch, setGlobalSearch] = useState('');
  const [selectedSocietes, setSelectedSocietes] = useState([]);
  const [selectedChantiers, setSelectedChantiers] = useState([]);
  const [kpiFilter, setKpiFilter] = useState('ALL');

  const isAdminOrResp = user?.role !== 'pointeur' && canAccess(user, 'materiels:write');
  const isAdmin       = user?.role !== 'pointeur' && canAccess(user, 'materiels:delete');

  const [formData, setFormData] = useState({
    matricule: '',
    type: '',
    marque: '',
    societe: '',
    compteur_prec: '',
    mode_compteur: 'h',
    chauffeur_nom: '',
    tel_chauffeur: '',
    cin_chauffeur: '',
    chantier_id: '',
    date_debut: '',
    assurance: '',
    taux_horaire: '',
    etat: 'en service',
    observation: '',
    date_vidange: '',

    date_visite_technique: '',
    cadence_visite_technique_mois: DEFAULTS_VISITE_TECHNIQUE.cadence,
    seuil_alerte_visite_technique_jours: DEFAULTS_VISITE_TECHNIQUE.seuilAlerte,

    date_echeance_visite_technique: '',
    date_echeance_vignette: '',
    date_echeance_feuille_circulation: '',
    date_echeance_assurance: '',
    date_echeance_mouchard: '',

    compteur_vidange_h: '',
    interval_vidange_h: DEFAULTS_H.interval,
    seuil_alerte_h: DEFAULTS_H.warn,
    seuil_critique_h: DEFAULTS_H.crit,
    tolerance_vidange_h: DEFAULTS_H.tol,

    compteur_vidange_km: '',
    interval_vidange_km: DEFAULTS_KM.interval,
    seuil_alerte_km: DEFAULTS_KM.warn,
    seuil_critique_km: DEFAULTS_KM.crit,
    tolerance_vidange_km: DEFAULTS_KM.tol
  });

  const norm = (s) =>
    String(s ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const getCompteurLabel = (row) => {
    const mode = normalizeModeCompteur(row?.mode_compteur);
    return mode === 'km' ? 'km' : 'h';
  };

  const getCycleValue = (row) => {
    const mode = normalizeModeCompteur(row?.mode_compteur);
    return Number(mode === 'km' ? row?.compteur_vidange_km || 0 : row?.compteur_vidange_h || 0);
  };

  const getIntervalValue = (row) => {
    const mode = normalizeModeCompteur(row?.mode_compteur);
    return Number(
      mode === 'km'
        ? row?.interval_vidange_km || DEFAULTS_KM.interval
        : row?.interval_vidange_h || DEFAULTS_H.interval
    );
  };

  const getWarnValue = (row) => {
    const mode = normalizeModeCompteur(row?.mode_compteur);
    return Number(
      mode === 'km'
        ? row?.seuil_alerte_km || DEFAULTS_KM.warn
        : row?.seuil_alerte_h || DEFAULTS_H.warn
    );
  };

  const getCritValue = (row) => {
    const mode = normalizeModeCompteur(row?.mode_compteur);
    return Number(
      mode === 'km'
        ? row?.seuil_critique_km || DEFAULTS_KM.crit
        : row?.seuil_critique_h || DEFAULTS_H.crit
    );
  };

  const formatValueWithUnit = (value, unit) => {
    const n = Number(value || 0);
    return `${n} ${unit}`;
  };

  const computeVidangeStatus = (row) => {
    const cycle = getCycleValue(row);
    const interval = getIntervalValue(row);
    const warn = getWarnValue(row);
    const crit = getCritValue(row);

    if (cycle >= interval) return 'DEPASSE';
    if (cycle >= crit) return 'CRITIQUE';
    if (cycle >= warn) return 'ALERTE';
    return 'OK';
  };

  const addMonths = (dateStr, months) => {
    const d = new Date(dateStr);
    d.setMonth(d.getMonth() + Number(months || 0));
    return d;
  };

  const computeVisiteTechnique = (row) => {
    if (!row?.date_echeance_visite_technique && !row?.date_visite_technique) {
      return { prochaine: null, joursRestants: null, statut: null };
    }

    const cadence = Number(row?.cadence_visite_technique_mois || DEFAULTS_VISITE_TECHNIQUE.cadence);
    const seuilAlerte = Number(
      row?.seuil_alerte_visite_technique_jours || DEFAULTS_VISITE_TECHNIQUE.seuilAlerte
    );

    const prochaine = row.date_echeance_visite_technique
      ? new Date(row.date_echeance_visite_technique)
      : addMonths(row.date_visite_technique, cadence);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    prochaine.setHours(0, 0, 0, 0);

    const joursRestants = Math.round((prochaine - today) / (1000 * 60 * 60 * 24));

    let statut = 'OK';
    if (joursRestants < 0) statut = 'DEPASSE';
    else if (joursRestants <= seuilAlerte) statut = 'ALERTE';

    return { prochaine, joursRestants, statut };
  };

  const getResumeCompteur = (row) => {
    const unit = getCompteurLabel(row);
    const cycle = getCycleValue(row);
    const interval = getIntervalValue(row);
    return `${cycle} / ${interval} ${unit}`;
  };

  const fetchData = async () => {
    setLoading(true);
    setToastError('');
    setToastSuccess('');

    try {
      const [matRes, chantierRes] = await Promise.all([
        axios.get('/materiels'),
        axios.get('/chantiers')
      ]);

      const matData = Array.isArray(matRes.data) ? matRes.data : [];
      setMateriels(matData);
      setChantiers(Array.isArray(chantierRes.data) ? chantierRes.data : []);

      if (matData.length) {
        const statsRes = await documentService.getDocumentsStats(
          'materiel',
          matData.map((m) => m.id)
        );
        setDocCounts(statsRes?.data || {});
      } else {
        setDocCounts({});
      }
    } catch (err) {
      console.error(err);
      setToastError('❌ Erreur chargement matériels ou chantiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const chantiersMap = useMemo(() => {
    const map = {};
    (chantiers || []).forEach((c) => {
      map[c.id] = c.name;
    });
    return map;
  }, [chantiers]);

  const materielsWithChantier = useMemo(() => {
    return (materiels || []).map((m) => {
      const mode_compteur = normalizeModeCompteur(m?.mode_compteur);
      const enriched = {
        ...m,
        mode_compteur,
        chantier_name: m?.chantier_id ? chantiersMap[m.chantier_id] || '' : '',
        documents_count: Number(docCounts[m.id] || 0)
      };

      const vt = computeVisiteTechnique(enriched);

      return {
        ...enriched,
        statut_vidange: m?.statut_vidange || computeVidangeStatus(enriched),
        statut_visite_technique: m?.statut_visite_technique || vt.statut,
        date_prochaine_visite_technique:
          m?.date_prochaine_visite_technique ||
          (vt.prochaine ? vt.prochaine.toISOString().split('T')[0] : null),
        jours_restants_visite_technique:
          m?.jours_restants_visite_technique ?? vt.joursRestants
      };
    });
  }, [materiels, chantiersMap, docCounts]);

  const societeOptions = useMemo(() => {
    const uniq = [...new Set((materielsWithChantier || []).map((m) => m?.societe).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b), 'fr', { sensitivity: 'base' }));

    return uniq.map((s) => ({
      value: s,
      label: s
    }));
  }, [materielsWithChantier]);

  const chantierOptions = useMemo(() => {
    const uniq = [...new Set((materielsWithChantier || []).map((m) => m?.chantier_name).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b), 'fr', { sensitivity: 'base' }));

    return uniq.map((s) => ({
      value: s,
      label: s
    }));
  }, [materielsWithChantier]);

  const filteredBaseData = useMemo(() => {
    const q = norm(globalSearch);
    const societes = selectedSocietes.map((s) => norm(s.value));
    const chantierNames = selectedChantiers.map((c) => norm(c.value));

    return (materielsWithChantier || []).filter((m) => {
      const societeOk =
        !societes.length ||
        societes.includes(norm(m?.societe || ''));

      const chantierOk =
        !chantierNames.length ||
        chantierNames.includes(norm(m?.chantier_name || ''));

      const searchOk = !q || norm([
        m?.id,
        m?.matricule,
        m?.type,
        m?.marque,
        m?.societe,
        m?.chauffeur_nom,
        m?.tel_chauffeur,
        m?.cin_chauffeur,
        m?.chantier_name,
        m?.etat,
        m?.observation,
        m?.date_vidange,
        m?.mode_compteur,
        m?.compteur_vidange_h,
        m?.interval_vidange_h,
        m?.compteur_vidange_km,
        m?.interval_vidange_km,
        m?.statut_vidange,
        m?.date_visite_technique,
        m?.statut_visite_technique,
        m?.statut_papiers,
        m?.documents_count
      ]
        .filter(Boolean)
        .join(' | ')).includes(q);

      return societeOk && chantierOk && searchOk;
    });
  }, [materielsWithChantier, globalSearch, selectedSocietes, selectedChantiers]);

  const filteredData = useMemo(() => {
    return filteredBaseData.filter((m) => {
      switch (kpiFilter) {
        case 'SERVICE':
          return norm(m?.etat).includes('service');
        case 'PANNE':
          return norm(m?.etat).includes('panne');
        case 'MAINTENANCE':
          return norm(m?.etat).includes('maintenance');
        case 'BLOQUE':
          return norm(m?.etat).includes('bloque');
        case 'MODE_H':
          return normalizeModeCompteur(m?.mode_compteur) === 'h';
        case 'MODE_KM':
          return normalizeModeCompteur(m?.mode_compteur) === 'km';
        case 'VIDANGE_OK':
          return m?.statut_vidange === 'OK';
        case 'VIDANGE_ALERTE':
          return m?.statut_vidange === 'ALERTE';
        case 'VIDANGE_CRITIQUE':
          return m?.statut_vidange === 'CRITIQUE';
        case 'VIDANGE_DEPASSE':
          return m?.statut_vidange === 'DEPASSE';
        case 'VT_OK':
          return m?.statut_visite_technique === 'OK';
        case 'VT_ALERTE':
          return m?.statut_visite_technique === 'ALERTE';
        case 'VT_DEPASSE':
          return m?.statut_visite_technique === 'DEPASSE';
        case 'PAPIERS_OK':
          return m?.statut_papiers === 'OK';
        case 'PAPIERS_ALERTE':
          return m?.statut_papiers === 'ALERTE';
        case 'PAPIERS_DEPASSE':
          return m?.statut_papiers === 'DEPASSE';
        case 'DOCS':
          return Number(m?.documents_count || 0) > 0;
        default:
          return true;
      }
    });
  }, [filteredBaseData, kpiFilter]);

  const kpi = useMemo(() => {
    const total = filteredBaseData.length;
    const enService = filteredBaseData.filter((m) => norm(m?.etat).includes('service')).length;
    const enPanne = filteredBaseData.filter((m) => norm(m?.etat).includes('panne')).length;
    const maintenance = filteredBaseData.filter((m) => norm(m?.etat).includes('maintenance')).length;
    const bloque = filteredBaseData.filter((m) => norm(m?.etat).includes('bloque')).length;

    const modeH = filteredBaseData.filter((m) => normalizeModeCompteur(m?.mode_compteur) === 'h').length;
    const modeKm = filteredBaseData.filter((m) => normalizeModeCompteur(m?.mode_compteur) === 'km').length;

    const okVidange = filteredBaseData.filter((m) => m.statut_vidange === 'OK').length;
    const alerteVidange = filteredBaseData.filter((m) => m.statut_vidange === 'ALERTE').length;
    const critiqueVidange = filteredBaseData.filter((m) => m.statut_vidange === 'CRITIQUE').length;
    const depasseVidange = filteredBaseData.filter((m) => m.statut_vidange === 'DEPASSE').length;

    const okVt = filteredBaseData.filter((m) => m.statut_visite_technique === 'OK').length;
    const alerteVt = filteredBaseData.filter((m) => m.statut_visite_technique === 'ALERTE').length;
    const depasseVt = filteredBaseData.filter((m) => m.statut_visite_technique === 'DEPASSE').length;

    const okPapiers = filteredBaseData.filter((m) => m.statut_papiers === 'OK').length;
    const alertePapiers = filteredBaseData.filter((m) => m.statut_papiers === 'ALERTE').length;
    const depassePapiers = filteredBaseData.filter((m) => m.statut_papiers === 'DEPASSE').length;

    const avecDocs = filteredBaseData.filter((m) => Number(m.documents_count || 0) > 0).length;

    return {
      total,
      enService,
      enPanne,
      maintenance,
      bloque,
      modeH,
      modeKm,
      okVidange,
      alerteVidange,
      critiqueVidange,
      depasseVidange,
      okVt,
      alerteVt,
      depasseVt,
      okPapiers,
      alertePapiers,
      depassePapiers,
      avecDocs
    };
  }, [filteredBaseData]);

  const rowClasses = (row) => {
    const e = norm(row?.etat);

    if (e.includes('bloque')) return 'mp-row-bloque';
    if (e.includes('panne')) return 'mp-row-panne';
    if (e.includes('maintenance')) return 'mp-row-maintenance';
    if (e.includes('service')) return 'mp-row-service';

    return '';
  };

  const openAddModal = () => {
    setFormMode('add');
    setSelected(null);
    setPdfError('');
    setToastError('');
    setToastSuccess('');
    setFormData({
      matricule: '',
      type: '',
      marque: '',
      societe: '',
      compteur_prec: '',
      mode_compteur: 'h',
      chauffeur_nom: '',
      tel_chauffeur: '',
      cin_chauffeur: '',
      chantier_id: '',
      date_debut: '',
      assurance: '',
      taux_horaire: '',
      etat: 'en service',
      observation: '',
      date_vidange: '',

      date_visite_technique: '',
      cadence_visite_technique_mois: DEFAULTS_VISITE_TECHNIQUE.cadence,
      seuil_alerte_visite_technique_jours: DEFAULTS_VISITE_TECHNIQUE.seuilAlerte,

      date_echeance_visite_technique: '',
      date_echeance_vignette: '',
      date_echeance_feuille_circulation: '',
      date_echeance_assurance: '',
      date_echeance_mouchard: '',

      compteur_vidange_h: '',
      interval_vidange_h: DEFAULTS_H.interval,
      seuil_alerte_h: DEFAULTS_H.warn,
      seuil_critique_h: DEFAULTS_H.crit,
      tolerance_vidange_h: DEFAULTS_H.tol,

      compteur_vidange_km: '',
      interval_vidange_km: DEFAULTS_KM.interval,
      seuil_alerte_km: DEFAULTS_KM.warn,
      seuil_critique_km: DEFAULTS_KM.crit,
      tolerance_vidange_km: DEFAULTS_KM.tol
    });
    setShowFormModal(true);
  };

  const openEditModal = (item) => {
    setFormMode('edit');
    setSelected(item);
    setPdfError('');
    setToastError('');
    setToastSuccess('');

    setFormData({
      matricule: item?.matricule ?? '',
      type: item?.type ?? '',
      marque: item?.marque ?? '',
      societe: item?.societe ?? '',
      compteur_prec: item?.compteur_prec ?? '',
      mode_compteur: normalizeModeCompteur(item?.mode_compteur),
      chauffeur_nom: item?.chauffeur_nom ?? '',
      tel_chauffeur: item?.tel_chauffeur ?? '',
      cin_chauffeur: item?.cin_chauffeur ?? '',
      chantier_id: item?.chantier_id ?? '',
      date_debut: item?.date_debut ? String(item.date_debut).split('T')[0] : '',
      assurance: item?.assurance ?? '',
      taux_horaire: item?.taux_horaire ?? '',
      etat: item?.etat ?? 'en service',
      observation: item?.observation ?? '',
      date_vidange: item?.date_vidange ? String(item.date_vidange).split('T')[0] : '',

      date_visite_technique: item?.date_visite_technique
        ? String(item.date_visite_technique).split('T')[0]
        : '',
      cadence_visite_technique_mois:
        item?.cadence_visite_technique_mois ?? DEFAULTS_VISITE_TECHNIQUE.cadence,
      seuil_alerte_visite_technique_jours:
        item?.seuil_alerte_visite_technique_jours ?? DEFAULTS_VISITE_TECHNIQUE.seuilAlerte,

      date_echeance_visite_technique: toDateInput(item?.date_echeance_visite_technique),
      date_echeance_vignette: toDateInput(item?.date_echeance_vignette),
      date_echeance_feuille_circulation: toDateInput(item?.date_echeance_feuille_circulation),
      date_echeance_assurance: toDateInput(item?.date_echeance_assurance),
      date_echeance_mouchard: toDateInput(item?.date_echeance_mouchard),

      compteur_vidange_h: item?.compteur_vidange_h ?? '',
      interval_vidange_h: item?.interval_vidange_h ?? DEFAULTS_H.interval,
      seuil_alerte_h: item?.seuil_alerte_h ?? DEFAULTS_H.warn,
      seuil_critique_h: item?.seuil_critique_h ?? DEFAULTS_H.crit,
      tolerance_vidange_h: item?.tolerance_vidange_h ?? DEFAULTS_H.tol,

      compteur_vidange_km: item?.compteur_vidange_km ?? '',
      interval_vidange_km: item?.interval_vidange_km ?? DEFAULTS_KM.interval,
      seuil_alerte_km: item?.seuil_alerte_km ?? DEFAULTS_KM.warn,
      seuil_critique_km: item?.seuil_critique_km ?? DEFAULTS_KM.crit,
      tolerance_vidange_km: item?.tolerance_vidange_km ?? DEFAULTS_KM.tol
    });

    setShowFormModal(true);
  };

  const openDeleteModal = (item) => {
    setSelected(item);
    setShowDeleteModal(true);
  };

  const openDocsModal = (item) => {
    setSelectedDocItem(item);
    setShowDocsModal(true);
  };

  const closeDocsModal = () => {
    setShowDocsModal(false);
    setSelectedDocItem(null);
    fetchData();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    if (
      [
        'chantier_id',
        'compteur_prec',
        'taux_horaire',
        'cadence_visite_technique_mois',
        'seuil_alerte_visite_technique_jours',
        'compteur_vidange_h',
        'interval_vidange_h',
        'seuil_alerte_h',
        'seuil_critique_h',
        'tolerance_vidange_h',
        'compteur_vidange_km',
        'interval_vidange_km',
        'seuil_alerte_km',
        'seuil_critique_km',
        'tolerance_vidange_km'
      ].includes(name)
    ) {
      setFormData({ ...formData, [name]: value === '' ? '' : Number(value) });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleModeCompteurChange = (e) => {
    const nextMode = normalizeModeCompteur(e.target.value);

    setFormData((prev) => ({
      ...prev,
      mode_compteur: nextMode,
      interval_vidange_h:
        nextMode === 'h'
          ? prev.interval_vidange_h || DEFAULTS_H.interval
          : prev.interval_vidange_h,
      seuil_alerte_h:
        nextMode === 'h'
          ? prev.seuil_alerte_h || DEFAULTS_H.warn
          : prev.seuil_alerte_h,
      seuil_critique_h:
        nextMode === 'h'
          ? prev.seuil_critique_h || DEFAULTS_H.crit
          : prev.seuil_critique_h,
      tolerance_vidange_h:
        nextMode === 'h'
          ? prev.tolerance_vidange_h || DEFAULTS_H.tol
          : prev.tolerance_vidange_h,
      interval_vidange_km:
        nextMode === 'km'
          ? prev.interval_vidange_km || DEFAULTS_KM.interval
          : prev.interval_vidange_km,
      seuil_alerte_km:
        nextMode === 'km'
          ? prev.seuil_alerte_km || DEFAULTS_KM.warn
          : prev.seuil_alerte_km,
      seuil_critique_km:
        nextMode === 'km'
          ? prev.seuil_critique_km || DEFAULTS_KM.crit
          : prev.seuil_critique_km,
      tolerance_vidange_km:
        nextMode === 'km'
          ? prev.tolerance_vidange_km || DEFAULTS_KM.tol
          : prev.tolerance_vidange_km
    }));
  };

  const handleFormSubmit = async () => {
    setToastError('');
    setToastSuccess('');

    if (!formData.matricule?.trim()) {
      setToastError('⚠️ Matricule est obligatoire.');
      return;
    }

    try {
      const payload = {
        matricule: formData.matricule,
        type: formData.type,
        marque: formData.marque,
        societe: formData.societe,
        compteur_prec: formData.compteur_prec,
        mode_compteur: normalizeModeCompteur(formData.mode_compteur),
        chauffeur_nom: formData.chauffeur_nom,
        tel_chauffeur: formData.tel_chauffeur,
        cin_chauffeur: formData.cin_chauffeur,
        chantier_id: formData.chantier_id === '' ? null : formData.chantier_id,
        date_debut: formData.date_debut,
        assurance: formData.assurance,
        taux_horaire: formData.taux_horaire,
        etat: formData.etat,
        observation: formData.observation,
        date_vidange: formData.date_vidange || null,

        date_visite_technique: formData.date_visite_technique || null,
        cadence_visite_technique_mois:
          formData.cadence_visite_technique_mois === ''
            ? DEFAULTS_VISITE_TECHNIQUE.cadence
            : formData.cadence_visite_technique_mois,
        seuil_alerte_visite_technique_jours:
          formData.seuil_alerte_visite_technique_jours === ''
            ? DEFAULTS_VISITE_TECHNIQUE.seuilAlerte
            : formData.seuil_alerte_visite_technique_jours,

        date_echeance_visite_technique: formData.date_echeance_visite_technique || null,
        date_echeance_vignette: formData.date_echeance_vignette || null,
        date_echeance_feuille_circulation: formData.date_echeance_feuille_circulation || null,
        date_echeance_assurance: formData.date_echeance_assurance || null,
        date_echeance_mouchard: formData.date_echeance_mouchard || null,

        compteur_vidange_h: formData.compteur_vidange_h === '' ? 0 : formData.compteur_vidange_h,
        interval_vidange_h: formData.interval_vidange_h === '' ? DEFAULTS_H.interval : formData.interval_vidange_h,
        seuil_alerte_h: formData.seuil_alerte_h === '' ? DEFAULTS_H.warn : formData.seuil_alerte_h,
        seuil_critique_h: formData.seuil_critique_h === '' ? DEFAULTS_H.crit : formData.seuil_critique_h,
        tolerance_vidange_h: formData.tolerance_vidange_h === '' ? DEFAULTS_H.tol : formData.tolerance_vidange_h,

        compteur_vidange_km: formData.compteur_vidange_km === '' ? 0 : formData.compteur_vidange_km,
        interval_vidange_km: formData.interval_vidange_km === '' ? DEFAULTS_KM.interval : formData.interval_vidange_km,
        seuil_alerte_km: formData.seuil_alerte_km === '' ? DEFAULTS_KM.warn : formData.seuil_alerte_km,
        seuil_critique_km: formData.seuil_critique_km === '' ? DEFAULTS_KM.crit : formData.seuil_critique_km,
        tolerance_vidange_km: formData.tolerance_vidange_km === '' ? DEFAULTS_KM.tol : formData.tolerance_vidange_km
      };

      if (formMode === 'add') {
        await axios.post('/materiels', payload);
        setToastSuccess('✅ Matériel ajouté.');
      } else {
        await axios.put(`/materiels/${selected.id}`, payload);
        setToastSuccess('✅ Matériel modifié.');
      }

      setShowFormModal(false);
      fetchData();
    } catch (err) {
      console.error(err);
      setToastError("❌ Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async () => {
    setToastError('');
    setToastSuccess('');
    try {
      await axios.delete(`/materiels/${selected.id}`);
      setShowDeleteModal(false);
      setToastSuccess('✅ Matériel supprimé.');
      fetchData();
    } catch (err) {
      console.error(err);
      setToastError('❌ Erreur suppression');
    }
  };

  const handleHistoriquePDF = async (row) => {
    setPdfError('');
    setPdfLoadingId(row?.id || null);

    try {
      const res = await axios.get('/materiel-reparations', {
        params: { id_materiel: row.id }
      });

      if (!res.data?.ok) {
        setPdfError(res.data?.message || "❌ Impossible de charger l'historique.");
        return;
      }

      const histRows = Array.isArray(res.data?.rows)
        ? res.data.rows
        : (Array.isArray(res.data?.data) ? res.data.data : []);

      if (!histRows.length) {
        setPdfError("ℹ️ Aucune réparation trouvée pour cet engin.");
        return;
      }

      const now = new Date();
      const userName = user?.username || user?.LOGIN || user?.name || 'Utilisateur';

      generateMaterielReparationsPDF({
        context: {
          generatedAt: now.toISOString(),
          userName,
          materiel: row
        },
        rows: histRows,
        chantiersMap
      });
    } catch (e) {
      console.error('PDF HIST ERROR:', e?.response?.data || e);
      setPdfError(
        e?.response?.data?.message ||
          e?.message ||
          '❌ Erreur lors de la génération du PDF Historique.'
      );
    } finally {
      setPdfLoadingId(null);
    }
  };

  const exportExcel = () => {
    const rows = filteredData.map((m) => {
      const unit = getCompteurLabel(m);

      return {
        ID: m.id,
        Matricule: m.matricule,
        Type: m.type,
        Marque: m.marque || '',
        Société: m.societe,
        Chantier: m.chantier_name || m.chantier_actuel || '',
        ModeCompteur: normalizeModeCompteur(m.mode_compteur),
        Unite: unit,
        Chauffeur: m.chauffeur_nom,
        Téléphone: m.tel_chauffeur,
        CIN: m.cin_chauffeur,
        CompteurActuel: m.compteur_prec,
        DateVidange: m.date_vidange || '',
        DateVisiteTechnique: m.date_visite_technique || '',
        CadenceVisiteTechniqueMois: m.cadence_visite_technique_mois ?? '',
        SeuilAlerteVisiteTechniqueJours: m.seuil_alerte_visite_technique_jours ?? '',
        ProchaineVisiteTechnique: m.date_prochaine_visite_technique || '',
        StatutVisiteTechnique: m.statut_visite_technique || '',
        EcheanceVignette: toDateInput(m.date_echeance_vignette),
        EcheanceFeuilleCirculation: toDateInput(m.date_echeance_feuille_circulation),
        EcheanceAssurance: toDateInput(m.date_echeance_assurance),
        EcheanceMouchard: toDateInput(m.date_echeance_mouchard),
        StatutPapiers: m.statut_papiers || '',
        CompteurVidangeH: m.compteur_vidange_h ?? '',
        IntervalVidangeH: m.interval_vidange_h ?? '',
        SeuilAlerteH: m.seuil_alerte_h ?? '',
        SeuilCritiqueH: m.seuil_critique_h ?? '',
        ToleranceVidangeH: m.tolerance_vidange_h ?? '',
        CompteurVidangeKM: m.compteur_vidange_km ?? '',
        IntervalVidangeKM: m.interval_vidange_km ?? '',
        SeuilAlerteKM: m.seuil_alerte_km ?? '',
        SeuilCritiqueKM: m.seuil_critique_km ?? '',
        ToleranceVidangeKM: m.tolerance_vidange_km ?? '',
        SuiviActif: getResumeCompteur(m),
        StatutVidange: m.statut_vidange,
        Documents: m.documents_count ?? 0,
        TauxHoraire: m.taux_horaire,
        Etat: m.etat,
        Observation: m.observation
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Materiels');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer]), 'materiels.xlsx');
  };

  const toggleKpiFilter = (code) => {
    setKpiFilter((prev) => (prev === code ? 'ALL' : code));
  };

  const resetAllFilters = () => {
    setGlobalSearch('');
    setSelectedSocietes([]);
    setSelectedChantiers([]);
    setKpiFilter('ALL');
  };

  const hasActiveFilters =
    !!globalSearch || selectedSocietes.length > 0 || selectedChantiers.length > 0 || kpiFilter !== 'ALL';

  // Engins avec au moins une alerte (vidange ou papiers) à traiter
  const nbAlertes = filteredBaseData.filter(
    (m) =>
      ['ALERTE', 'CRITIQUE', 'DEPASSE'].includes(m.statut_vidange) ||
      ['ALERTE', 'DEPASSE'].includes(m.statut_papiers)
  ).length;

  const etatCards = [
    { key: 'ALL',         title: 'Total',       value: kpi.total,       color: '#6d4e36', icon: FaTruck,               description: 'Nombre de matériels correspondant aux filtres (société, chantier, recherche).' },
    { key: 'SERVICE',     title: 'En service',  value: kpi.enService,   color: '#2e7d32', icon: FaCheckCircle,         description: 'Matériels dont l’état est « en service ».' },
    { key: 'MAINTENANCE', title: 'Maintenance', value: kpi.maintenance, color: '#f57c00', icon: FaWrench,              description: 'Matériels dont l’état est « en maintenance ».' },
    { key: 'PANNE',       title: 'En panne',    value: kpi.enPanne,     color: '#b71c1c', icon: FaExclamationTriangle, description: 'Matériels dont l’état est « en panne ».', alert: kpi.enPanne > 0 },
    { key: 'BLOQUE',      title: 'Bloqué',      value: kpi.bloque,      color: '#37474f', icon: FaLock,                description: 'Matériels bloqués : exclus du suivi vidange.' }
  ];

  const statusPanels = [
    {
      title: 'Vidange',
      icon: FaOilCan,
      description: 'Cycle depuis la dernière vidange comparé aux seuils du matériel : Alerte ≥ seuil alerte, Critique ≥ seuil critique, Dépassé ≥ intervalle.',
      items: [
        { key: 'VIDANGE_OK',       label: 'OK',       value: kpi.okVidange,       color: '#2e7d32' },
        { key: 'VIDANGE_ALERTE',   label: 'Alerte',   value: kpi.alerteVidange,   color: '#f9a825' },
        { key: 'VIDANGE_CRITIQUE', label: 'Critique', value: kpi.critiqueVidange, color: '#e65100' },
        { key: 'VIDANGE_DEPASSE',  label: 'Dépassé',  value: kpi.depasseVidange,  color: '#b71c1c' }
      ]
    },
    {
      title: 'Visite technique',
      icon: FaClipboardCheck,
      description: 'Échéance saisie, sinon dernière visite + cadence. Approche = dans le seuil d’alerte (30 j par défaut), Dépassée = échéance passée.',
      items: [
        { key: 'VT_OK',      label: 'OK',       value: kpi.okVt,      color: '#2e7d32' },
        { key: 'VT_ALERTE',  label: 'Approche', value: kpi.alerteVt,  color: '#f9a825' },
        { key: 'VT_DEPASSE', label: 'Dépassée', value: kpi.depasseVt, color: '#b71c1c' }
      ]
    },
    {
      title: 'Papiers véhicule',
      icon: FaFileAlt,
      description: 'Pire statut parmi visite technique, vignette, feuille de circulation, assurance et mouchard. Échéance proche = 30 jours ou moins.',
      items: [
        { key: 'PAPIERS_OK',      label: 'À jour',  value: kpi.okPapiers,      color: '#2e7d32' },
        { key: 'PAPIERS_ALERTE',  label: 'Proche',  value: kpi.alertePapiers,  color: '#f9a825' },
        { key: 'PAPIERS_DEPASSE', label: 'Expirés', value: kpi.depassePapiers, color: '#b71c1c' }
      ]
    }
  ];

  const quickChips = [
    { key: 'MODE_H',  label: 'Mode heure', icon: FaClock,     value: kpi.modeH },
    { key: 'MODE_KM', label: 'Mode km',    icon: FaRoad,      value: kpi.modeKm },
    { key: 'DOCS',    label: 'Avec docs',  icon: FaPaperclip, value: kpi.avecDocs }
  ];

  const kpiLabel =
    [
      ...etatCards.map((c) => ({ key: c.key, label: c.title })),
      ...statusPanels.flatMap((p) => p.items.map((i) => ({ key: i.key, label: `${p.title} : ${i.label}` }))),
      ...quickChips
    ].find((c) => c.key === kpiFilter)?.label || kpiFilter;

  const GRAVITE = { DEPASSE: 3, CRITIQUE: 2, ALERTE: 1, OK: 0 };
  const STATUT_COLOR = { OK: '#2e7d32', ALERTE: '#f9a825', CRITIQUE: '#e65100', DEPASSE: '#b71c1c' };
  const PAPIER_SHORT = {
    visite_technique: 'VT',
    vignette: 'VIG',
    feuille_circulation: 'FC',
    assurance: 'ASS',
    mouchard: 'MOU'
  };

  const etatInfo = (etat) => {
    const e = norm(etat);
    if (e.includes('bloque')) return { label: 'Bloqué', cls: 'bloque' };
    if (e.includes('panne')) return { label: 'En panne', cls: 'panne' };
    if (e.includes('maintenance')) return { label: 'Maintenance', cls: 'maintenance' };
    if (e.includes('service')) return { label: 'En service', cls: 'service' };
    return { label: etat || '—', cls: 'autre' };
  };

  const columns = [
    {
      dataField: 'matricule',
      text: 'Véhicule',
      sort: true,
      formatter: (cell, row) => (
        <div className="mp-veh">
          <div className="mp-veh-mat">{cell || `#${row.id}`}</div>
          <div className="mp-veh-sub">
            {[row?.type, row?.marque].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
      )
    },
    {
      dataField: 'societe',
      text: 'Société / Chantier',
      sort: true,
      formatter: (cell, row) => {
        const chantier =
          row?.chantier_name || row?.chantier_actuel || (row?.chantier_id ? `#${row.chantier_id}` : '');
        return (
          <div className="mp-cell-stack">
            <div className="fw-semibold">{cell || '—'}</div>
            {chantier && (
              <small className="text-muted">
                <FaMapMarkerAlt className="me-1" />
                {chantier}
              </small>
            )}
          </div>
        );
      }
    },
    {
      dataField: 'chauffeur_nom',
      text: 'Chauffeur',
      sort: true,
      formatter: (cell, row) =>
        cell ? (
          <div className="mp-cell-stack">
            <div>{cell}</div>
            {row?.tel_chauffeur && (
              <small className="text-muted">
                <FaPhoneAlt className="me-1" />
                {row.tel_chauffeur}
              </small>
            )}
          </div>
        ) : (
          <span className="text-muted">—</span>
        )
    },
    {
      dataField: 'compteur_prec',
      text: 'Compteur',
      sort: true,
      sortValue: (cell) => Number(cell || 0),
      formatter: (cell, row) => {
        const isKm = normalizeModeCompteur(row?.mode_compteur) === 'km';
        return (
          <div className="mp-cell-stack">
            <div className="fw-semibold">
              {cell === null || cell === undefined || cell === ''
                ? '—'
                : formatValueWithUnit(cell, getCompteurLabel(row))}
            </div>
            <small className={`mp-mode mp-mode-${isKm ? 'km' : 'h'}`}>
              {isKm ? <FaRoad className="me-1" /> : <FaClock className="me-1" />}
              {isKm ? 'Kilomètres' : 'Heures'}
            </small>
          </div>
        );
      }
    },
    {
      dataField: 'statut_vidange',
      text: 'Vidange',
      sort: true,
      sortValue: (_, row) => getCycleValue(row) / (getIntervalValue(row) || 1),
      formatter: (cell, row) => {
        const cycle = getCycleValue(row);
        const interval = getIntervalValue(row);
        const pct = Math.min(100, Math.round((cycle / (interval || 1)) * 100));
        const color = STATUT_COLOR[cell] || STATUT_COLOR.OK;
        return (
          <div className="mp-vidange" title={`Statut : ${cell || 'OK'}`}>
            <div className="d-flex justify-content-between mp-vidange-txt">
              <span>{getResumeCompteur(row)}</span>
              <span style={{ color, fontWeight: 800 }}>{pct}%</span>
            </div>
            <div className="mp-progress">
              <div className="mp-progress-bar" style={{ width: `${pct}%`, background: color }} />
            </div>
            <small className="text-muted">
              Dernière : {row?.date_vidange ? toDateInput(row.date_vidange) : '—'}
            </small>
          </div>
        );
      }
    },
    {
      dataField: 'statut_papiers',
      text: 'Papiers',
      sort: true,
      sortValue: (cell) => GRAVITE[cell] ?? -1,
      formatter: (_, row) => (
        <div className="mp-papiers">
          {PAPIERS.map(({ key, label }) => {
            const date = key === 'visite_technique'
              ? row?.date_prochaine_visite_technique
              : row?.[`date_echeance_${key}`];
            const statut = row?.[`statut_${key}`];
            const jours = row?.[`jours_restants_${key}`];
            const title = date
              ? `${label} : ${toDateInput(date)}${Number.isFinite(jours) ? ` (${jours < 0 ? `expiré depuis ${-jours} j` : `${jours} j restants`})` : ''}`
              : `${label} : non renseigné`;
            return (
              <span
                key={key}
                className={`mp-papier mp-papier-${date ? (statut || 'OK').toLowerCase() : 'vide'}`}
                title={title}
              >
                {PAPIER_SHORT[key]}
              </span>
            );
          })}
        </div>
      )
    },
    {
      dataField: 'etat',
      text: 'État',
      sort: true,
      formatter: (cell) => {
        const { label, cls } = etatInfo(cell);
        return <span className={`mp-etat mp-etat-${cls}`}>{label}</span>;
      }
    },
    {
      dataField: 'actions',
      text: 'Actions',
      isDummyField: true,
      headerStyle: { width: '190px' },
      formatter: (_, row) => (
        <div className="mp-actions">
          <button
            type="button"
            className="mp-icon-btn"
            title="Documents"
            onClick={() => openDocsModal(row)}
          >
            <FaPaperclip />
            {Number(row?.documents_count || 0) > 0 && (
              <span className="mp-icon-count">{row.documents_count}</span>
            )}
          </button>

          {isAdminOrResp && (
            <>
              <button
                type="button"
                className="mp-icon-btn"
                title="Historique réparations (PDF)"
                onClick={() => handleHistoriquePDF(row)}
                disabled={pdfLoadingId === row.id}
              >
                {pdfLoadingId === row.id ? <Spinner size="sm" /> : <FaFilePdf />}
              </button>

              <button
                type="button"
                className="mp-icon-btn mp-icon-btn-primary"
                title="Modifier"
                onClick={() => openEditModal(row)}
              >
                <FaEdit />
              </button>

              {isAdmin && (
                <button
                  type="button"
                  className="mp-icon-btn mp-icon-btn-danger"
                  title="Supprimer"
                  onClick={() => openDeleteModal(row)}
                >
                  <FaTrash />
                </button>
              )}
            </>
          )}
        </div>
      )
    }
  ];

  const isModeKmForm = normalizeModeCompteur(formData.mode_compteur) === 'km';
  const unitForm = isModeKmForm ? 'km' : 'h';

  return (
    <div className="container-fluid px-3 px-lg-4 mt-3 mb-5 mp-page">
      {/* ── En-tête ── */}
      <div className="mp-hero">
        <div className="mp-hero-title">
          <span className="mp-hero-icon"><FaTruck /></span>
          <div>
            <h2>Parc matériel</h2>
            <div className="mp-hero-sub">
              {kpi.total} engin{kpi.total > 1 ? 's' : ''} · {kpi.enService} en service
              {nbAlertes > 0 && (
                <span className="mp-hero-alert">
                  <FaExclamationTriangle className="me-1" />
                  {nbAlertes} à traiter
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mp-hero-actions">
          <Button variant="light" onClick={fetchData} title="Actualiser">
            <FaSyncAlt className="me-2" />
            Actualiser
          </Button>
          <Button
            variant="light"
            onClick={exportExcel}
            disabled={loading || filteredData.length === 0}
          >
            <FaFileExcel className="me-2" />
            Excel
          </Button>
          {isAdminOrResp && (
            <Button variant="warning" className="mp-btn-add" onClick={openAddModal}>
              <FaPlus className="me-2" />
              Ajouter
            </Button>
          )}
        </div>
      </div>

      {pdfError && <Alert variant={pdfError.startsWith('ℹ️') ? 'info' : 'danger'} dismissible onClose={() => setPdfError('')}>{pdfError}</Alert>}
      {toastError && <Alert variant="danger" dismissible onClose={() => setToastError('')}>{toastError}</Alert>}
      {toastSuccess && <Alert variant="success" dismissible onClose={() => setToastSuccess('')}>{toastSuccess}</Alert>}

      {/* ── KPI état ── */}
      <div className="mp-kpi-grid mb-3">
        {etatCards.map((card) => (
          <KpiCard
            key={card.key}
            title={card.title}
            description={card.description}
            value={card.value}
            color={card.color}
            icon={card.icon}
            active={kpiFilter === card.key}
            alert={card.alert}
            onClick={() => toggleKpiFilter(card.key)}
          />
        ))}
      </div>

      {/* ── Panneaux d'alertes ── */}
      <div className="mp-status-grid mb-3">
        {statusPanels.map((panel) => {
          const total = panel.items.reduce((s, i) => s + i.value, 0);
          const PanelIcon = panel.icon;
          return (
            <div className="mp-status-panel" key={panel.title}>
              <div className="mp-status-head">
                <span className="mp-status-icon"><PanelIcon /></span>
                <span className="mp-status-title">
                  {panel.title}
                  <InfoTip text={panel.description} />
                </span>
                <span className="mp-status-total">{total} suivi{total > 1 ? 's' : ''}</span>
              </div>

              <div className="mp-segbar">
                {total === 0 ? (
                  <div className="mp-segbar-empty" />
                ) : (
                  panel.items
                    .filter((i) => i.value > 0)
                    .map((i) => (
                      <div
                        key={i.key}
                        style={{ flex: i.value, background: i.color }}
                        title={`${i.label} : ${i.value}`}
                      />
                    ))
                )}
              </div>

              <div className="mp-status-items">
                {panel.items.map((i) => (
                  <button
                    type="button"
                    key={i.key}
                    className={`mp-status-chip${kpiFilter === i.key ? ' active' : ''}`}
                    style={{ '--chip': i.color }}
                    onClick={() => toggleKpiFilter(i.key)}
                  >
                    <span className="mp-dot" />
                    {i.label}
                    <b>{i.value}</b>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Filtres ── */}
      <div className="mp-toolbar mb-3">
        <div className="mp-toolbar-row">
          <div className="mp-search">
            <FaSearch className="mp-search-icon" />
            <Form.Control
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              placeholder="Rechercher matricule, marque, chauffeur, chantier…"
            />
          </div>
          <div className="mp-select">
            <Select
              isMulti
              isClearable
              isSearchable
              options={societeOptions}
              value={selectedSocietes}
              onChange={(val) => setSelectedSocietes(val || [])}
              placeholder={<span><FaBuilding className="me-2" />Sociétés</span>}
            />
          </div>
          <div className="mp-select">
            <Select
              isMulti
              isClearable
              isSearchable
              options={chantierOptions}
              value={selectedChantiers}
              onChange={(val) => setSelectedChantiers(val || [])}
              placeholder={<span><FaMapMarkerAlt className="me-2" />Chantiers</span>}
            />
          </div>
        </div>

        <div className="mp-toolbar-row mp-toolbar-foot">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            {quickChips.map((c) => {
              const ChipIcon = c.icon;
              return (
                <button
                  type="button"
                  key={c.key}
                  className={`mp-quick-chip${kpiFilter === c.key ? ' active' : ''}`}
                  onClick={() => toggleKpiFilter(c.key)}
                >
                  <ChipIcon className="me-1" />
                  {c.label}
                  <b>{c.value}</b>
                </button>
              );
            })}
          </div>

          <div className="d-flex gap-2 flex-wrap align-items-center">
            {kpiFilter !== 'ALL' && (
              <span className="mp-active-filter">
                <FaFilter className="me-1" />
                {kpiLabel}
                <button type="button" onClick={() => setKpiFilter('ALL')} title="Retirer ce filtre">
                  <FaTimes />
                </button>
              </span>
            )}
            {hasActiveFilters && (
              <button type="button" className="mp-link-btn" onClick={resetAllFilters}>
                <FaBroom className="me-1" />
                Réinitialiser
              </button>
            )}
            <span className="mp-count">
              {filteredData.length} résultat{filteredData.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* ── Tableau ── */}
      {loading ? (
        <div className="mp-loading">
          <Spinner animation="border" />
          <span>Chargement du parc…</span>
        </div>
      ) : (
        <div className="mp-table-wrap">
          <BootstrapTable
            keyField="id"
            data={filteredData}
            columns={columns}
            pagination={paginationFactory({
              sizePerPage: 20,
              sizePerPageList: [
                { text: '20', value: 20 },
                { text: '50', value: 50 },
                { text: '100', value: 100 }
              ],
              showTotal: true,
              alwaysShowAllBtns: true
            })}
            hover
            condensed
            bordered={false}
            rowClasses={rowClasses}
            noDataIndication={
              <div className="mp-empty">
                <FaTruck />
                <div>Aucun matériel ne correspond aux filtres.</div>
              </div>
            }
            wrapperClasses="table-responsive"
          />
        </div>
      )}

      {/* ── Formulaire ── */}
      <SimpleModal
        show={showFormModal}
        title={
          <span className="d-flex align-items-center gap-2">
            {formMode === 'add' ? <FaPlus /> : <FaEdit />}
            {formMode === 'add' ? 'Ajouter un matériel' : `Modifier ${selected?.matricule || 'le matériel'}`}
          </span>
        }
        onClose={() => setShowFormModal(false)}
        footer={
          <>
            <Button variant="outline-secondary" onClick={() => setShowFormModal(false)}>
              Annuler
            </Button>
            <Button variant="primary" onClick={handleFormSubmit}>
              Enregistrer
            </Button>
          </>
        }
      >
        <Form>
          <Row className="g-3">
            <Col lg={6}>
              <FormSection icon={FaTruck} title="Identification">
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Label>Matricule *</Form.Label>
                    <Form.Control name="matricule" value={formData.matricule} onChange={handleFormChange} placeholder="Ex : 46163-B-72" />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Type</Form.Label>
                    <Form.Control name="type" value={formData.type} onChange={handleFormChange} placeholder="Ex : Camion, Pelle…" />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Marque</Form.Label>
                    <Form.Control name="marque" value={formData.marque} onChange={handleFormChange} placeholder="Ex : IVECO, MASTER…" />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Société</Form.Label>
                    <Form.Control name="societe" value={formData.societe} onChange={handleFormChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>État</Form.Label>
                    <Form.Select name="etat" value={formData.etat} onChange={handleFormChange}>
                      <option value="en service">En service</option>
                      <option value="en panne">En panne</option>
                      <option value="en maintenance">En maintenance</option>
                      <option value="bloqué">Bloqué</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Taux horaire</Form.Label>
                    <Form.Control type="number" name="taux_horaire" value={formData.taux_horaire} onChange={handleFormChange} />
                  </Col>
                </Row>
              </FormSection>
            </Col>

            <Col lg={6}>
              <FormSection icon={FaUser} title="Chauffeur">
                <Row className="g-2">
                  <Col md={12}>
                    <Form.Label>Nom</Form.Label>
                    <Form.Control name="chauffeur_nom" value={formData.chauffeur_nom} onChange={handleFormChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Téléphone</Form.Label>
                    <Form.Control name="tel_chauffeur" value={formData.tel_chauffeur} onChange={handleFormChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>CIN</Form.Label>
                    <Form.Control name="cin_chauffeur" value={formData.cin_chauffeur} onChange={handleFormChange} />
                  </Col>
                </Row>
              </FormSection>

              <FormSection icon={FaMapMarkerAlt} title="Affectation & compteur">
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Label>Chantier actuel</Form.Label>
                    <Form.Select name="chantier_id" value={formData.chantier_id} onChange={handleFormChange}>
                      <option value="">-- Sélectionner --</option>
                      {chantiers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Date début</Form.Label>
                    <Form.Control type="date" name="date_debut" value={formData.date_debut} onChange={handleFormChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Mode compteur</Form.Label>
                    <Form.Select name="mode_compteur" value={formData.mode_compteur} onChange={handleModeCompteurChange}>
                      <option value="h">Heures (engins chantier)</option>
                      <option value="km">Kilomètres (camions / véhicules)</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Compteur actuel ({unitForm})</Form.Label>
                    <Form.Control type="number" name="compteur_prec" value={formData.compteur_prec} onChange={handleFormChange} />
                  </Col>
                </Row>
              </FormSection>
            </Col>

            <Col lg={12}>
              <FormSection icon={FaFileAlt} title="Papiers du véhicule (dates d'échéance)">
                <Row className="g-2">
                  <Col md={4}>
                    <Form.Label>Assurance (compagnie / police)</Form.Label>
                    <Form.Control name="assurance" value={formData.assurance} onChange={handleFormChange} />
                  </Col>
                  {PAPIERS.filter((p) => p.key !== 'visite_technique').map(({ key, label }) => (
                    <Col md={2} sm={6} key={key}>
                      <Form.Label>{label}</Form.Label>
                      <Form.Control
                        type="date"
                        name={`date_echeance_${key}`}
                        value={formData[`date_echeance_${key}`]}
                        onChange={handleFormChange}
                      />
                    </Col>
                  ))}
                </Row>
                <small className="text-muted d-block mt-2">
                  Alerte « échéance proche » 30 jours avant, « expiré » une fois la date passée.
                </small>
              </FormSection>
            </Col>

            <Col lg={6}>
              <FormSection icon={FaClipboardCheck} title="Visite technique">
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Label>Échéance</Form.Label>
                    <Form.Control
                      type="date"
                      name="date_echeance_visite_technique"
                      value={formData.date_echeance_visite_technique}
                      onChange={handleFormChange}
                    />
                    <small className="text-muted">Prioritaire sur le calcul date + cadence.</small>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Dernière visite</Form.Label>
                    <Form.Control
                      type="date"
                      name="date_visite_technique"
                      value={formData.date_visite_technique}
                      onChange={handleFormChange}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Cadence (mois)</Form.Label>
                    <Form.Control
                      type="number"
                      name="cadence_visite_technique_mois"
                      value={formData.cadence_visite_technique_mois}
                      onChange={handleFormChange}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Alerte approche (jours)</Form.Label>
                    <Form.Control
                      type="number"
                      name="seuil_alerte_visite_technique_jours"
                      value={formData.seuil_alerte_visite_technique_jours}
                      onChange={handleFormChange}
                    />
                  </Col>
                </Row>
              </FormSection>
            </Col>

            <Col lg={6}>
              <FormSection icon={FaOilCan} title={`Suivi vidange (${isModeKmForm ? 'km' : 'heures'})`}>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Label>Date dernière vidange</Form.Label>
                    <Form.Control type="date" name="date_vidange" value={formData.date_vidange} onChange={handleFormChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Cycle en cours ({unitForm})</Form.Label>
                    <Form.Control
                      type="number"
                      name={`compteur_vidange_${unitForm}`}
                      value={formData[`compteur_vidange_${unitForm}`]}
                      onChange={handleFormChange}
                      disabled={!isAdmin}
                    />
                    {!isAdmin && <small className="text-muted">Lecture seule hors admin.</small>}
                  </Col>
                  <Col md={3} sm={6}>
                    <Form.Label>Intervalle</Form.Label>
                    <Form.Control type="number" name={`interval_vidange_${unitForm}`} value={formData[`interval_vidange_${unitForm}`]} onChange={handleFormChange} />
                  </Col>
                  <Col md={3} sm={6}>
                    <Form.Label>Seuil alerte</Form.Label>
                    <Form.Control type="number" name={`seuil_alerte_${unitForm}`} value={formData[`seuil_alerte_${unitForm}`]} onChange={handleFormChange} />
                  </Col>
                  <Col md={3} sm={6}>
                    <Form.Label>Seuil critique</Form.Label>
                    <Form.Control type="number" name={`seuil_critique_${unitForm}`} value={formData[`seuil_critique_${unitForm}`]} onChange={handleFormChange} />
                  </Col>
                  <Col md={3} sm={6}>
                    <Form.Label>Tolérance</Form.Label>
                    <Form.Control type="number" name={`tolerance_vidange_${unitForm}`} value={formData[`tolerance_vidange_${unitForm}`]} onChange={handleFormChange} />
                  </Col>
                </Row>
              </FormSection>
            </Col>

            <Col lg={12}>
              <FormSection icon={FaStickyNote} title="Observation">
                <Form.Control as="textarea" name="observation" rows={3} value={formData.observation} onChange={handleFormChange} />
              </FormSection>
            </Col>
          </Row>
        </Form>
      </SimpleModal>

      <SimpleModal
        show={showDeleteModal}
        title="Supprimer le matériel"
        onClose={() => setShowDeleteModal(false)}
        footer={
          <>
            <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
              Annuler
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              <FaTrash className="me-2" />
              Supprimer
            </Button>
          </>
        }
      >
        Confirmer la suppression de <strong>{selected?.matricule}</strong> ? Cette action est irréversible.
      </SimpleModal>

      <DocumentsModal
        show={showDocsModal}
        onHide={closeDocsModal}
        module="materiel"
        moduleId={selectedDocItem?.id}
        itemLabel={
          selectedDocItem
            ? `${selectedDocItem.matricule || ''}${selectedDocItem.type ? ` - ${selectedDocItem.type}` : ''}`
            : ''
        }
        title="Documents du matériel"
        canDelete={isAdminOrResp}
      />
    </div>
  );
};

export default MaterielPage;
